import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { FileUploader } from "react-drag-drop-files";
import { FormProvider, useController, useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import { DocumentPreviewDialog } from "../../components/CommonComponents/DialogConfirmation/DocPreviewDialog";
import { DownloadButton, PaymentStatusButton, UploadButton, ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";
import Input from "../../components/CommonComponents/Input/Input";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { ErrorToast } from "../../components/toastMessage";
import { BASE_URL } from "../../constants/Constants";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import { isLevel2 } from "../../hoc/isLevel2";
import { TApplication, useServiceCertificateConditionField, useNameOfServiceField, } from "../../hooks/applications.hooks";
import {
  TApplicationField,
  TApplicationFieldOption,
} from "../../hooks/application-fields.hooks";
import { useToggle } from "../../hooks/common";
import {
  TSavedDemandNoteData,
  useApplicationForLevel2,
  useLevel2AllReviews,
} from "../../hooks/level-2-application-approval.hooks";
import {
  TCertificateData,
  TLevel2PaymentReceipt,
  useApplicationReceiptsForLevel2,
  useDemandNoteData,
  useGenerateCertificateForLevel2,
  useLevel2CertificateData,
  useLevel2LastPaymentReview,
  useLevel2PaymentRejectApplication,
  useLevel2PaymentReturnApplication,
  useLevel2SaveCertificateData,
  useLevel2UpdateReceiptStatus,
  useSaveLevel2PaymentReview,
} from "../../hooks/level-2-payment-approval.hooks";
import { useAutoFillDemandNoteData } from "../../hooks/level-3-application-approval.hooks";
import uploadImage, { uploadPlotShape } from "../../utils/azure-storage-blob";
import { downloadResource } from "../../utils/common";
import { formatDate } from "../../utils/date";
import style from "../superAdmin/logo/logo.module.css";
import { RenderAllReviews } from "./RenderAllReviews";
import SelectDirection, { availableDirections } from "./SelectDirection";
import SelectPlotShape from "./SelectPlotShape";
import { _axios } from "../../utils/axios";

export const LevelTwoPaymentApproval = isLevel2(() => {
  const location = useLocation();
  const [screen, setScreen] = useState<
    "review" | "generate-doc" | "upload-map"
  >("review");

  const id = +location.state?.id;

  const {
    isError: isError1,
    isFetching: isFetching1,
    data: application,
  } = useApplicationForLevel2({ id });

  const receiptsQuery = useApplicationReceiptsForLevel2({ id });
  const demandNoteDataQuery = useDemandNoteData({ applicationId: id });

  const autoFill = useAutoFillDemandNoteData();

  const serviceQuery = useNameOfServiceField();
  const nameOfService = serviceQuery.data?.options?.length
    ? serviceQuery.data.options.find(
      (e) => e?.id === application?.name_of_service
    )
    : "";

  const condition = useServiceCertificateConditionField({
    name_of_service: (nameOfService as TApplicationFieldOption)?.name,
  });
  console.log("name", nameOfService)

  const isFetching =
    isFetching1 || receiptsQuery.isLoading || demandNoteDataQuery.isLoading;

  const receipts = receiptsQuery.data;
  const demandNoteData = demandNoteDataQuery.data;

  const isError =
    isError1 ||
    demandNoteDataQuery.isError ||
    receiptsQuery.isError ||
    (!isFetching && (!application || !receipts?.length || !demandNoteData?.id));

  const setStuff = useSetStuffToRenderOnNavbar();

  useEffect(() => {
    setStuff(
      <div className="md:ml-auto md:mr-8">
        <p className="text-grey-600">
          <span className="w-28 font-semibold inline-block">
            Application ID
          </span>
          <span className="text-grey-600 font-semibold mx-2">:</span>
          <span>{application?.applicationId || "Nil"}</span>
        </p>
        <p className="text-grey-600 flex items-center">
          <span className="w-28 text-grey-600 font-semibold inline-block">
            Status
          </span>
          <span className="text-grey-600 font-semibold mx-2">:</span>
          <span className="text-primary">{application?.status || "Nil"}</span>
        </p>
      </div>
    );
    return () => setStuff(null);
  }, [setStuff, application]);

  if (isNaN(id)) {
    return <Navigate to={"/user-applications"} replace />;
  }

  if (
    isFetching ||
    isError ||
    !application ||
    !demandNoteData ||
    !receipts?.length
  ) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  let toRender = null;

  switch (screen) {
    case "review": {
      toRender = (
        <Review
          receipts={receipts}
          application={application}
          demandNote={demandNoteData}
          onNext={() => setScreen("generate-doc")}
          allDocsApproved={receiptsQuery.data?.every(
            (e) => e?.payment_status !== "Payment Discrepancy"
          )}
        />
      );
      break;
    }
    case "generate-doc": {
      toRender = (
        <GenerateCertificateWrapper
          application={application}
          autoFill={autoFill}
          condition={condition?.data?.[0] || {}}
          onBack={() => setScreen("review")}
          onForward={() => setScreen("upload-map")}
        />
      );
      break;
    }
    case "upload-map": {
      toRender = (
        <UploadMapWrapper
          application={application}
          onBack={() => setScreen("review")}
          onForward={() => { }}
        />
      );
      break;
    }
  }

  return (
    <>
      <div className="px-2 pb-2 sm:p-4">
        {/* <div className="bg-white p-4 sm:p-8 rounded-2xl">{toRender}</div> */}
        <div className={`${style.IFrameOutCss} bg-white p-4 sm:p-8 rounded-xl shadow-lg border border-gray-300`}>{toRender}</div>
      </div>
    </>
  );
});

type GenerateCertificateProps = {
  onBack: () => unknown;
  application: TApplication & Record<string, unknown>;
  autoFill?: any;
  condition?: any;
  onForward: () => unknown;
};

function GenerateCertificateWrapper({
  application,
  autoFill,
  condition,
  onBack,
  onForward,
}: GenerateCertificateProps) {
  const lastReviewQuery = useLevel2LastPaymentReview({ id: application.id });
  const reviewId = lastReviewQuery.data?.id;
  const savedCertificateDataQuery = useLevel2CertificateData({
    applicationId: application.id,
    reviewId: reviewId,
  });

  const isFetching =
    lastReviewQuery.isFetching || savedCertificateDataQuery.isFetching;
  const isError =
    lastReviewQuery.isError || savedCertificateDataQuery.isError || !reviewId;

  if (isFetching || isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  return (
    <GenerateCertificate
      reviewId={reviewId}
      application={application}
      autoFill={autoFill}
      condition={condition}
      onBack={onBack}
      onForward={onForward}
      defaultValues={savedCertificateDataQuery.data ?? undefined}
    />
  );
}

function GenerateCertificate({
  defaultValues,
  application,
  autoFill,
  condition,
  onBack,
  reviewId,
  onForward,
}: GenerateCertificateProps & {
  defaultValues?: TCertificateData;
  reviewId: number;
}) {
  const navigate = useNavigate();
  const [open, toggleSelectShape] = useToggle();
  const [openDirection, toggleDirection] = useToggle();
  const [pdfToView, setPdfToView] = useState<null | string>(null);
  const [documents, setDocuments] = useState([]);
  const forwardMutation = useGenerateCertificateForLevel2();
  const saveDataMutation = useLevel2SaveCertificateData();
  useEffect(() => {
    _axios
      .get(`${BASE_URL}/api/level-three-documents/doclist/${application.id}`)
      .then(function (res) {
        setDocuments(res.data.data);
      })
      .catch(function (error) {
        // handle error
        console.log(error);
      });
  }, []);
  const formMethods = useForm({
    defaultValues: {
      ab: 0,
      bc: 0,
      cd: 0,
      da: 0,
      builtUpArea: 0,
      regularizedArea: 0,
      demandNoteAmountPaid: 0,
      plotAreaAsPerSaleDeed: 0,
      areaToSurrenderToNagarParishad: 0,
      areaOfConstructionToBeDemolished: 0,
      applicantName: application.plot_owner_s_name || "",
      applicationNumber: application?.applicationId || "",
      constructionType: application.construction_type || "",
      layoutName: application.unauthorized_layout_name || "",
      mauja: application.mauja || "",
      plotNo: application.plot_no || "",
      ...defaultValues,
      applicationSubmissionDate:
        defaultValues?.applicationSubmissionDate || application?.submittedAt
          ? dayjs(
            defaultValues?.applicationSubmissionDate ||
            application?.submittedAt
          ).format("YYYY-MM-DD")
          : null,
      khasaraNo: application?.khasara_no || "",
      municipality: autoFill?.data?.[0]?.municipal_council_name || "",
      tehsil: autoFill?.data?.[0]?.tahasil_name || "",
      district: autoFill?.data?.[0]?.district_name || "",
      logo: autoFill?.data?.[0]?.certificate_logo || "",
      headerText: autoFill?.data?.[0]?.certificate_header_text || "",
      conditions: condition?.status === 1 ? condition?.condition : "",
    },
    mode: "onBlur",
    shouldFocusError: true,
  });

  const { formState } = formMethods;

  const plotShape = useController({
    control: formMethods.control,
    name: "plotShape",
    defaultValue: defaultValues?.plotShape,
    rules: { required: "* Required" },
  });

  const direction = useController({
    control: formMethods.control,
    name: "direction",
    defaultValue: defaultValues?.direction,
    rules: { required: "* Required" },
  });

  const [isEditableConditions, setIsEditableConditions] = useState(false);
  const toggleEditableConditions = () => {
    setIsEditableConditions((prev) => !prev);
  };

  return (
    <FormProvider {...formMethods}>
      <h4
        className={`text-center text-grey-600 font-semibold mb-8 text-2xl ${pdfToView ? "hidden" : ""
          }`}
      >
        Certificate Fields
      </h4>

      <form
        id="create-certificate"
        className={`grid grid-cols-2 gap-4 ${pdfToView ? "hidden" : ""}`}
        noValidate
        onSubmit={formMethods.handleSubmit((data) =>
          saveDataMutation.mutate(
            {
              applicationId: application.id,
              reviewId,
              data,
            },
            {
              onSuccess: (result) => {
                if (Array.isArray(result?.data?.data)) {
                  const blob = new Blob(
                    [new Uint8Array(result.data.data).buffer],
                    { type: "application/pdf" }
                  );
                  setPdfToView(URL.createObjectURL(blob));
                }
              },
            }
          )
        )}
      >
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Applicant Name"
            placeholder="Applicant Name"
            {...formMethods.register("applicantName")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Application Number"
            placeholder="Application Number"
            {...formMethods.register("applicationNumber")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            type="date"
            label="Application Submission Date"
            placeholder="Application Submission Date"
            {...formMethods.register("applicationSubmissionDate", {
              valueAsDate: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Layout Name"
            placeholder="Layout Name"
            {...formMethods.register("layoutName")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Mauza"
            placeholder="Mauza"
            {...formMethods.register("mauja")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Khasara No"
            placeholder="Khasara No"
            {...formMethods.register("khasaraNo")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Plot No"
            placeholder="Plot No"
            {...formMethods.register("plotNo")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();

              e.stopPropagation();

              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Demand Note Amount Paid"
            placeholder="Demand Note Amount Paid"
            {...formMethods.register("demandNoteAmountPaid", {
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            label="Receipt Number"
            placeholder="Receipt Number"
            {...formMethods.register("receiptNumber")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            type="date"
            label="Payment Receipt Date"
            placeholder="Payment Receipt Date"
            {...formMethods.register("paymentReceiptDate", { valueAsDate: true })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            label="Use for which the plot is regularized"
            placeholder="Use for which the plot is regularized"
            {...formMethods.register("constructionType")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();

              e.stopPropagation();

              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Plot Area As Per Sale Deed [sq. mt.]"
            placeholder="Plot Area As Per Sale Deed [sq. mt.]"
            {...formMethods.register("plotAreaAsPerSaleDeed", {
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();

              e.stopPropagation();

              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Regularized Area [sq. mt.]"
            placeholder="Regularized Area [sq. mt.]"
            {...formMethods.register("regularizedArea", {
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();

              e.stopPropagation();

              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Built-up Area Regularized [sq. mt.]"
            placeholder="Built-up Area Regularized [sq. mt.]"
            {...formMethods.register("builtUpArea", {
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            label="Address"
            placeholder="Address"
            {...formMethods.register("address")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            required
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();

              e.stopPropagation();

              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Plot Area To Be Surrendered To Nagar Parishad"
            placeholder="Plot Area To Be Surrendered To Nagar Parishad"
            error={!!formState.errors?.areaToSurrenderToNagarParishad}
            helperText={formState?.errors?.areaToSurrenderToNagarParishad?.message?.toString()}
            {...formMethods.register("areaToSurrenderToNagarParishad", {
              required: "* Required",
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            required
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();

              e.stopPropagation();

              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Area Of Construction To Be Demolished"
            placeholder="Area Of Construction To Be Demolished"
            error={!!formState.errors?.areaOfConstructionToBeDemolished}
            helperText={formState?.errors?.areaOfConstructionToBeDemolished?.message?.toString()}
            {...formMethods.register("areaOfConstructionToBeDemolished", {
              required: "* Required",
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            label="Permit No"
            placeholder="Permit No"
            {...formMethods.register("permitNo")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            required
            label="Schematic Dimension: AB [mtr]"
            placeholder="Schematic Dimension: AB [mtr]"
            error={!!formState.errors?.ab}
            helperText={formState?.errors?.ab?.message?.toString()}
            {...formMethods.register("ab", {
              required: "* Required",
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            required
            label="Schematic Dimension: BC [mtr]"
            placeholder="Schematic Dimension: BC [mtr]"
            error={!!formState.errors?.bc}
            helperText={formState?.errors?.bc?.message?.toString()}
            {...formMethods.register("bc", {
              required: "* Required",
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            required
            label="Schematic Dimension: CD [mtr]"
            placeholder="Schematic Dimension: CD [mtr]"
            error={!!formState.errors?.cd}
            helperText={formState?.errors?.cd?.message?.toString()}
            {...formMethods.register("cd", {
              required: "* Required",
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            required
            label="Schematic Dimension: DA [mtr]"
            placeholder="Schematic Dimension: DA [mtr]"
            error={!!formState.errors?.da}
            helperText={formState?.errors?.da?.message?.toString()}
            {...formMethods.register("da", {
              required: "* Required",
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Municipal Council Name"
            placeholder="Municipal Council Name"
            error={!!formState.errors?.municipality}
            helperText={formState?.errors?.municipality?.message?.toString()}
            {...formMethods.register("municipality", {
              required: "* Required",
            })}
          />
        </div>

        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Office Address: Tahasil"
            placeholder="Office Address: Tahasil"
            error={!!formState.errors?.tehsil}
            helperText={formState?.errors?.tehsil?.message?.toString()}
            {...formMethods.register("tehsil", { required: "* Required" })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Office Address: District"
            placeholder="Office Address: District"
            error={!!formState.errors?.district}
            helperText={formState?.errors?.district?.message?.toString()}
            {...formMethods.register("district", { required: "* Required" })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          {/* div for balancing UI */}
        </div>
        <div className="col-span-2 md:col-span-1">
          <label className="text-base font-bold mb-2">Select Map</label>
          <div className="d-flex">
            <button
              type="button"
              onClick={toggleSelectShape}
              className={`border border-gray-300 w-full rounded-xl p-4 sm:p-8 flex justify-center items-center ${plotShape.fieldState.error ? "border-red-500" : ""
                }`}
            >
              <i className="fa fa-upload m-0 text-blue-500 text-xl sm:text-2xl"></i>
            </button>
            <FileUploader
              handleChange={async (image: any) => {
                // setFormData({ ...formData, gov_id_document: e }) 
                const plot_shape = await uploadPlotShape(image, application?.applicationId + new Date().toISOString(), 'Plot_Shape');
                plotShape.field.onChange(plot_shape);
              }}
              name="file"
              types={["JPG", "PNG"]}
              children={
                <div className={style.uploadContainerDocument}>
                  <p className={`${style.linkCss} md:w-full lg:w-full`}>
                    {'OR'}
                  </p>
                  <label className={style.uploadLable} >Browse</label>
                </div>
              } />
          </div>
          {!!plotShape?.field?.value && (
            <img
              className="rounded-lg mx-auto mt-2 border border-gray-200"
              src={String(plotShape.field.value)}
              width={150}
              height={150}
              alt="Plot Shape"
            />
          )}

          {!!plotShape.fieldState?.error?.message && (
            <p className="mt-1 text-red-500">
              {plotShape.fieldState.error.message.toString()}
            </p>
          )}
        </div>
        <div className="col-span-2 md:col-span-1">
          <label className="text-base font-bold mb-2">Select Direction</label>
          {/* <button
            type="button"
            onClick={toggleDirection}
            className={`border border-gray-300 w-full rounded-xl p-8 flex justify-center items-center ${
              direction.fieldState.error ? "border-red-500" : ""
            }`}
          >
            <i className="fa fa-upload m-0 text-blue-500 text-2xl"></i>
          </button> */}
          <button
            type="button"
            onClick={toggleDirection}
            className={`border border-gray-300 w-full rounded-xl p-4 sm:p-8 flex justify-center items-center ${plotShape.fieldState.error ? "border-red-500" : ""
              }`}
          >
            <i className="fa fa-upload m-0 text-blue-500 text-xl sm:text-2xl"></i>
          </button>
          {!!direction?.field?.value && (
            <img
              className="rounded-lg mx-auto mt-2 border border-gray-200"
              src={
                availableDirections.find(
                  (e) => e.id === String(direction.field.value)
                )?.direction ?? ""
              }
              width={150}
              height={150}
              alt="Plot Shape"
            />
          )}
          {!!direction.fieldState?.error?.message && (
            <p className="mt-1 text-red-500">
              {direction.fieldState.error.message.toString()}
            </p>
          )}
        </div>
        <div className="col-span-2 md:col-span-1 pt-2">
          <label className="text-base font-bold mb-2">Copy To</label>
          <textarea
            placeholder="Copy To"
            {...formMethods.register("copyTo")}
            rows={5}
          />
        </div>
        <div className="col-span-2 md:col-span-1 pt-2">
          <label className="text-base font-bold mb-2">ULC Comment</label>
          <textarea
            placeholder="ULC Comment"
            {...formMethods.register("ulcComment")}
            rows={5}
          />
        </div>
        <div className="col-span-2">
          <label className="flex items-center">
            <span className="text-base font-bold mb-2">
              Conditions For Certificate
            </span>
            <i
              className={`fa ${isEditableConditions ? "fa-lock" : "fa-pencil"
                } cursor-pointer ml-2`}
              onClick={toggleEditableConditions}
            ></i>
          </label>
          <textarea
            placeholder="Conditions for Certificate"
            {...formMethods.register("conditions")}
            rows={5}
            className={`h-auto w-100 ${isEditableConditions ? "bg-white" : "bg-gray-200"
              }`}
            disabled={!isEditableConditions}
          />
        </div>
        <div className="col-span-2 flex-wrap md:flex-nowrap flex justify-center gap-4 items-center mt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={saveDataMutation.isPending || forwardMutation.isPending}
            className="bg-black px-5 md:px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          >
            Back
          </button>
          <button
            form="create-certificate"
            type="submit"
            disabled={saveDataMutation.isPending || forwardMutation.isPending}
            className="bg-primary px-5 md:px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          >
            {"Save And Preview"}
          </button>
        </div>
      </form>
      {!!pdfToView && (
        <>
          {/* <iframe
            title="PDF Viewer"
            className="w-full h-full min-h-screen"
            src={pdfToView + "#toolbar=0"}
          /> */}
          <iframe
            title="PDF Viewer"
            // className="w-full h-full min-h-screen hidden sm:block"
            className={style.IFrameCss}
            src={pdfToView + "#toolbar=0"}
          />
          <div className="flex items-center justify-center sm:hidden py-36 bg-gray-200 flex-col gap-4">
            <p>Certificate</p>
            <a
              className="inline-block text-white py-2 px-4 rounded-full bg-blue-500"
              download={`${application.applicationId}-Certificate.pdf`}
              href={pdfToView}
            >
              Download
            </a>
          </div>
          <div className="col-span-2 flex justify-center space-x-4 items-center mt-8">
            <button
              type="button"
              onClick={() => setPdfToView(null)}
              disabled={saveDataMutation.isPending || forwardMutation.isPending}
              className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              Back
            </button>
            <button
              type="submit"
              onClick={() => {
                if (documents?.length === 0) {
                  forwardMutation.mutate(
                    { applicationId: application.id, reviewId },
                    { onSuccess: () => navigate("/level-2-applications") }
                  );
                } else onForward();
              }}
              disabled={saveDataMutation.isPending || forwardMutation.isPending}
              className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              {forwardMutation.isPending ? "Next" : "Next"}
            </button>
          </div>
        </>
      )}
      {open && ((
        <SelectPlotShape
          onClose={toggleSelectShape}
          selected={plotShape.field.value || ""}
          onSelect={(n) => {
            plotShape.field.onChange(n);
            toggleSelectShape();
          }}
        />
      ))}
      {openDirection && (
        <SelectDirection
          onClose={toggleDirection}
          selected={direction.field.value || ""}
          onSelect={(n) => {
            direction.field.onChange(n);
            toggleDirection();
          }}
        />
      )}
    </FormProvider>
  );
}
function UploadMapWrapper({
  application,
  onBack,
}: GenerateCertificateProps & {
  defaultValues?: TCertificateData;
}) {
  const fileTypes = ["JPG", "PNG", "GIF", "PDF"];
  const navigate = useNavigate();
  const [file, setFile] = useState<any[]>([]);
  const [files, setFiles] = useState<{ [key: number]: File }>({});
  const [isModal, setIsModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>();
  const [documents, setDocuments] = useState<any>([]);
  const lastReviewQuery = useLevel2LastPaymentReview({ id: application.id });
  const [previewDocument, setPreviewDocument] = useState<{ url: string } | null>(null);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  useEffect(() => {
    _axios
      .get(
        `${BASE_URL}/api/level-three-documents/doclistpermitable/:userId/${application.id}`
      )
      .then(function (res) {
        setDocuments(res.data.data);
        setDocumentsLoaded(true);
      })
      .catch(function (error) {
        console.log(error);
        setDocumentsLoaded(true);
      });
  }, []);

  const reviewId = lastReviewQuery.data?.id;
  const savedCertificateDataQuery = useLevel2CertificateData({
    applicationId: application.id,
    reviewId: reviewId,
  });
  const forwardMutation = useGenerateCertificateForLevel2();
  const saveDataMutation = useLevel2SaveCertificateData();

  // const handleFile = (e: any, index: number) => {
  //   if (!file.some((item) => item.id === index))
  //     setFile([...file, { id: index, file: e }]);
  // };

  const handleFile = (e: File, index: number) => {
    setFiles((prevFiles) => {
      const newFiles = { ...prevFiles, [index]: e };
      console.log(`File uploaded for index ${index}:`, e.name);
      console.log(
        "Current files state:",
        Object.keys(newFiles).map((key) => ({
          index: key,
          fileName: newFiles[Number(key)].name,
        }))
      );
      return newFiles;
    });
  };

  const isFetching =
    lastReviewQuery.isFetching || savedCertificateDataQuery.isFetching;
  const isError =
    lastReviewQuery.isError || savedCertificateDataQuery.isError || !reviewId;

  if (isFetching || isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  // const onUploadAndForward = async () => {
  //   if (file?.length != documents?.length) {
  //     ErrorToast("Please Sign all documents");
  //   } else {
  //     let urls = await Promise.all(
  //       file.map(async (data) => {
  //         return {
  //           id: documents[data.id].id,
  //           file: await uploadFileToBlob(
  //             data.file,
  //             "application-documents",
  //             "application_id",
  //             application.applicationId
  //           ),
  //         };
  //       })
  //     );
  const onUploadAndForward = async () => {
    if (Object.keys(files).length !== documents?.length) {
      ErrorToast("Please Sign all documents");
      return;
    }

    try {
      let urls = await Promise.all(
        documents.map(
          async (doc: { id: any; document_name: string }, index: number) => {
            const file = files[index];
            if (!file) {
              throw new Error(`No file found for document at index ${index}`);
            }
            const uniqueFileName = `${doc.document_name.replace(
              /\s+/g,
              "_"
            )}_${Date.now()}`;
            const uploadedUrl = await uploadImage(
              file,
              uniqueFileName,
              // "application-documents", changing subcontainerfile name for task no #434
              "application_id",
              application.applicationId
            );
            console.log(`Uploaded URL for index ${index}:`, uploadedUrl);
            return {
              id: doc.id,
              file: uploadedUrl,
            };
          }
        )
      );

      console.log("Upload payload data:", urls);

      const response = await _axios.post(
        `${BASE_URL}/api/digital-signature-docs/save-map`,
        { urls: urls }
      );
      console.log("API response:", response.data);

      forwardMutation.mutate(
        { applicationId: application.id, reviewId },
        { onSuccess: () => navigate("/level-2-applications") }
      );
    } catch (error) {
      console.error("Error in onUploadAndForward:", error);
      ErrorToast("An error occurred while uploading and forwarding");
    }
  };

  const documentColumns = [
    {
      name: "Document Name",
      // selector: (d: any) => d.document_name,
      cell: (d: any) => <span className="text-center">{d.document_name}</span>,
      center: true,
      grow: 3,
    },
    {
      name: "Upload",
      center: true,
      width: "300px",
      cell: (d: any, index: number) =>
        files[index] ? (
          <div className="flex flex-col items-center sm:flex-row sm:items-center">
            {/* <button
              className="text-lg mb-2 sm:mb-0 sm:mr-2"
              onClick={() => {
                setSelectedDoc({
                  file: files[index],
                });
                setIsModal(true);
              }}
            >
              <i className="m-0 fa fa-upload"></i>
            </button> */}
            <UploadButton
              onClick={() => {
                setSelectedDoc({
                  file: files[index],
                });
                setIsModal(true);
              }}
            />
            <span className="text-center sm:text-left max-w-[200px] ml-2">
              {files[index].name}
            </span>
          </div>
        ) : (
          <FileUploader
            handleChange={(e: File) => handleFile(e, index)}
            name={`file-${index}`}
            types={fileTypes}
          >
            <div className={`${style.singleUpload} w-full`}>
              <div className="flex justify-center rounded-md border-inherit border p-2">
                <button className="px-4 py-2 text-white bg-yellow-500 rounded-lg text-sm sm:text-base sm:px-7">
                  Browse
                </button>
              </div>
            </div>
          </FileUploader>
        ),
    },
    {
      name: "View",
      center: true,
      grow: 0.5,

      cell: (d: any) => (
        <div className="flex space-x-4">
          {/* <button
            onClick={() => openInNewTab(String(d?.document))}
            className="text-lg"
          >
            <i className="m-0 fa fa-eye"></i>
          </button>
          <button
            onClick={() => downloadResource(String(d?.document))}
            className="text-lg"
          >
            <i className="m-0 fa fa-download"></i>
          </button> */}
          {/* <ViewButton
            onClick={() => openInNewTab(String(d?.document))}
          /> */}
          <ViewButton
            onClick={() => {
              console.log("View button clicked", d?.document);
              setPreviewDocument({ url: String(d?.document) });
            }}
          />
          <DownloadButton
            onClick={() => downloadResource(String(d?.document))}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      {documentsLoaded && documents.length > 0 ? (
        <>
          <h2 className="text-2xl mb-10">Upload Map After Digital Signature</h2>
          <DataTable
            keyField="url"
            className="mt-4"
            data={(documents as any) ?? []}
            customStyles={tableCustomStyles}
            columns={documentColumns}
          />
          <div className="col-span-2 flex justify-center space-x-4 items-center mt-8">
            <button
              type="button"
              onClick={onBack}
              disabled={saveDataMutation.isPending || forwardMutation.isPending}
              className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              Back
            </button>
            <button
              form="create-demand-note"
              type="button"
              onClick={onUploadAndForward}
              disabled={Object.keys(files).length !== documents?.length}
              className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              {forwardMutation.isPending ? "Forwarding" : "Upload and Forward"}
            </button>
          </div>
        </>
      ) : documentsLoaded ? (
        <>
          <h2 className="text-2xl mb-10">Upload Map After Digital Signature</h2>
          <div className="col-span-2 flex flex-col items-center mt-8">
            <span className="text-1xl mb-10">
              There are no records to display
            </span>
            <div className="col-span-2 flex justify-center space-x-4 items-center mt-8">
              <button
                type="button"
                onClick={onBack}
                disabled={
                  saveDataMutation.isPending || forwardMutation.isPending
                }
                className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
              >
                Back
              </button>
              <button
                form="create-demand-note"
                type="button"
                onClick={() =>
                  forwardMutation.mutate(
                    { applicationId: application.id, reviewId },
                    { onSuccess: () => navigate("/level-2-applications") }
                  )
                }
                className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
              >
                {forwardMutation.isPending ? "Forwarding" : "Forward"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col justify-center items-center p-8">
          <h2 className="text-2xl mb-10">Upload Map After Digital Signature</h2>
          <SpinnerCircular />
        </div>
      )}

      <DocumentPreviewDialog onClose={() => setIsModal(false)} open={isModal}>
        {selectedDoc && (
          <Worker workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}>
            <Viewer
              fileUrl={selectedDoc?.document ?? URL.createObjectURL(selectedDoc?.file)}
            />
          </Worker>
        )}
      </DocumentPreviewDialog>

      <DocumentPreviewDialog
        onClose={() => setPreviewDocument(null)}
        open={!!previewDocument}
      >
        {previewDocument && previewDocument.url && (
          previewDocument.url.endsWith("pdf") ? (
            <Worker workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}>
              <Viewer fileUrl={previewDocument.url} />
            </Worker>
          ) : (
            <img
              src={previewDocument.url}
              className="object-contain w-full h-full"
              alt="Document"
            />
          )
        )}
      </DocumentPreviewDialog>


      {/* <Dialog onClose={() => setIsModal(false)} open={isModal}>
        <div style={{ width: "1000px", height: "80vh", overflow: "hidden" }}>
          <div className="p-4 flex justify-center items-center">
            <h4 className="m-0 grow text-center text-2xl font-bold">
              Document
            </h4>
            <button className="text-xl" onClick={() => setIsModal(false)}>
              <i className="fa fa-close"></i>
            </button>
          </div>
          {selectedDoc && (
            <div style={{ height: "100%", width: "100%" }}>
              <Worker
                workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
              >
                <Viewer
                  fileUrl={
                    selectedDoc?.document ??
                    URL.createObjectURL(selectedDoc?.file)
                  }
                />
              </Worker>
            </div>
          )}
        </div>
      </Dialog> */}
    </div>
  );
}

type ReviewProps = {
  onNext: () => unknown;
  application: TApplication & Record<string, unknown>;
  allDocsApproved?: boolean;
  receipts: TLevel2PaymentReceipt[];
  demandNote: { id: number } & TSavedDemandNoteData;
};

function KeyValue({
  label,
  value,
}: {
  label: string;
  value?: string | number;
}) {
  return (
    <div className="flex items-center flex-wrap sm:flex-nowrap">
      <p className="text-grey-600 text-xs sm:text-base font-bold shrink-0 sm:basis-1/2 mr-6">
        {label}
      </p>
      <p className="text-grey-600 text-xs sm:text-base grow">{value || "--"}</p>
    </div>
  );
}

function Review({
  allDocsApproved,
  onNext,
  application,
  demandNote,
  receipts,
}: ReviewProps) {
  const [document, setDocument] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const navigate = useNavigate();
  const [reviewsDialog, toggleReviewsDialog] = useToggle();
  const allReviews = useLevel2AllReviews({ id: application.id });
  const saveReviewMutation = useSaveLevel2PaymentReview();
  const lastReviewQuery = useLevel2LastPaymentReview({ id: application.id });
  const [comment, setComment] = useState("");
  const updateReceiptStatusMutation = useLevel2UpdateReceiptStatus();
  const rejectMutation = useLevel2PaymentRejectApplication();
  const returnMutation = useLevel2PaymentReturnApplication();

  const id = application.id;

  const disableBackBtn =
    lastReviewQuery.isFetching ||
    saveReviewMutation.isPending ||
    rejectMutation.isPending ||
    returnMutation.isPending ||
    updateReceiptStatusMutation.isPending;

  const disabled =
    disableBackBtn || application.status !== "Pending Payment Level 2 Review";

  const lastReviewData = lastReviewQuery.data;

  useEffect(() => {
    setComment(lastReviewData?.comment_text || "");
  }, [lastReviewData]);

  const { mutate: updateStatus } = updateReceiptStatusMutation;

  const receiptsTable = useMemo(
    (): TableColumn<TLevel2PaymentReceipt>[] => [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
        grow: 0.5,
      },
      {
        name: "Application ID",
        selector: (d) => d.application_id,
        center: true,
        grow: 2,
      },
      {
        name: "Receipt No",
        selector: (d) => String(d?.receipt_no || "-"),
        center: true,
        width: "150px",
      },
      {
        name: "Paid Amount",
        selector: (d) => (d?.amount_paid ? `${d.amount_paid} INR` : "-"),
        center: true,
        width: "160px",
      },
      {
        name: "Payment Date",
        center: true,
        cell: (d) => (d?.payment_date ? formatDate(d.payment_date) : "-"),
        width: "160px",
      },
      {
        name: "Receipt",
        center: true,
        cell: (d, i) => (
          <div className="flex space-x-4">
            {/* <button
              className="text-lg"
              onClick={() =>
                setDocument({
                  url: String(d?.receipt_file),
                  name: String(d?.receipt_no || "Payment Receipt"),
                })
              }
            >
              <i className="m-0 fa fa-eye"></i>
            </button>
            <button
              onClick={() => downloadResource(String(d?.receipt_file))}
              className="text-lg"
            >
              <i className="m-0 fa fa-download"></i>
            </button> */}
            <ViewButton
              onClick={() =>
                setDocument({
                  url: String(d?.receipt_file),
                  name: String(d?.receipt_no || "Payment Receipt"),
                })
              }
            />
            <DownloadButton
              onClick={() => downloadResource(String(d?.receipt_file))}
            />
          </div>
        ),
      },
      {
        name: "Action",
        center: true,
        cell: (d, i) => (
          // <button
          //   className="text-lg"
          //   disabled={disabled}
          //   onClick={() => {
          //     updateStatus({
          //       applicationId: id,
          //       receiptId: d.receipt_id,
          //       status:
          //         d?.payment_status === "Payment Discrepancy"
          //           ? "Payment Acknowledgment"
          //           : "Payment Discrepancy",
          //     });
          //   }}
          // >
          //   {d.payment_status === "Payment Discrepancy" ? (
          //     <i className="m-0 fa fa-window-close-o text-red-500"></i>
          //   ) : (
          //     <i className="m-0 fa fa-check-square-o text-green-400"></i>
          //   )}
          // </button>
          <PaymentStatusButton
            paymentStatus={d.payment_status}
            disabled={disabled}
            onClick={() => {
              updateStatus({
                applicationId: id,
                receiptId: d.receipt_id,
                status: d.payment_status === "Payment Discrepancy"
                  ? "Payment Acknowledgment"
                  : "Payment Discrepancy",
              });
            }}
            tooltipText="Toggle Payment Status"
          />
        ),
      },
    ],
    [disabled, updateStatus, id]
  );

  if (lastReviewQuery.isLoading || lastReviewQuery.isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {lastReviewQuery.isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  return (
    <>
      <div>
        <h4 className="text-2xl text-center font-bold mb-4">Payment Details</h4>
        <div className="flex justify-between-items-center flex-wrap md:flex-nowrap mb-4 gap-4">
          <div className="basis-full md:basis-1/2 grow space-y-4">
            <KeyValue
              label="1. Applicant Name"
              value={demandNote.applicantName}
            />
            <KeyValue label="2. Layout Name" value={demandNote.layoutName} />
            <KeyValue label="3. Mauza" value={demandNote.mauja} />
            <KeyValue label="4. Khasara" value={demandNote.khasaraNo} />
            <KeyValue
              label="5. Plot Area"
              value={
                demandNote.area
                  ? `${demandNote.area} ${demandNote.unit_area}`
                  : "-"
              }
            />
            <KeyValue
              label="6. Built-up Area [sq. mt.]"
              value={demandNote.constructionArea}
            />
          </div>
          <div className="basis-full md:basis-1/2 grow space-y-4">
            <KeyValue
              label="7. Due Date"
              value={demandNote.dueDate ? formatDate(demandNote.dueDate) : ""}
            />
            <KeyValue label="8. Plot No" value={demandNote.plotNo} />
            <KeyValue
              label="9. Particular 1 INR"
              value={demandNote.particular1Total}
            />
            <KeyValue
              label="10. Particular 2 INR"
              value={demandNote.particular2Total}
            />
            <KeyValue
              label="11. Scrutiny Charges"
              value={demandNote.scrutinyCharges}
            />
            <KeyValue
              label="12. Total Amount of Demand INR"
              value={demandNote.totalAmount}
            />
          </div>
        </div>
        <DataTable
          data={receipts}
          keyField="receipt_id"
          columns={receiptsTable}
          customStyles={tableCustomStyles}
          // className="border border-gray-300 mb-4"
          className="mb-4"
        />
        <div className="grid grid-cols-7 gap-4 md:gap-8">
          <div className="col-span-7 sm:col-span-5 lg:col-span-5">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm md:text-base font-bold">
                Level 2 Reviewer Remark <span className="text-red-500">*</span>
              </label>
              <button
                className="text-sm md:text-base font-bold disabled:text-gray-500"
                disabled={!allReviews.data?.length}
                onClick={toggleReviewsDialog}
              >
                <i className="fa fa-history"></i>
                Previous Reviews
              </button>
            </div>
            <textarea
              placeholder="Add Comment"
              value={comment}
              disabled={disabled}
              rows={5}
              className="h-auto w-100"
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
          <div className="flex sm:flex-col col-span-7 sm:col-span-2 lg:col-span-2 gap-3 mt-8">
            <button
              type="button"
              disabled={disabled || !lastReviewData?.id || !allDocsApproved}
              onClick={onNext}
              className="bg-primary py-2 text-sm md:text-base rounded-lg font-bold text-white px-3 basis-1/2 sm:basis-auto disabled:bg-gray-300 disabled:text-gray-500"
            >
              Approve & Next
            </button>
            <button
              type="button"
              disabled={
                disabled || !lastReviewQuery.data?.id || allDocsApproved
              }
              className="bg-red-400 py-2 text-sm md:text-base rounded-lg font-bold text-white px-3 basis-1/2 sm:basis-auto disabled:bg-gray-300 disabled:text-gray-500"
              onClick={() =>
                returnMutation.mutate(
                  {
                    applicationId: application.id,
                    application_id: application.applicationId,
                    user_id: application.userId,
                  },
                  { onSuccess: () => navigate("/level-2-applications") }
                )
              }
            >
              {returnMutation.isPending ? "Returning" : "Return"}
            </button>
            <button
              type="button"
              disabled={disabled || !lastReviewQuery.data?.id}
              className="bg-red-600 py-2 text-sm md:text-base rounded-lg font-bold text-white px-3 basis-1/2 sm:basis-auto disabled:bg-gray-300 disabled:text-gray-500"
              onClick={() =>
                rejectMutation.mutate(
                  {
                    applicationId: application.id,
                    application_id: application.applicationId,
                    user_id: application.userId,
                  },
                  { onSuccess: () => navigate("/level-2-applications") }
                )
              }
            >
              {rejectMutation.isPending ? "Rejecting" : "Reject"}
            </button>
          </div>
          <div className="col-span-7 flex flex-wrap md:flex-nowrap justify-center gap-4 items-center">
            <button
              type="button"
              disabled={disableBackBtn}
              onClick={() => navigate(-1)}
              className="bg-black px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              Back
            </button>
            <button
              type="button"
              disabled={disabled || !comment?.trim()}
              onClick={() => {
                saveReviewMutation.mutate({
                  applicationId: application.id,
                  comment,
                });
              }}
              className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              {saveReviewMutation.isPending ? "Saving" : "Save"}
            </button>
          </div>
        </div>
      </div>
      {reviewsDialog && (
        <RenderAllReviews
          onClose={toggleReviewsDialog}
          reviews={allReviews.data || []}
        />
      )}
      {!!document && (
        <DocumentPreviewDialog
          onClose={() => setDocument(null)}
          open={!!document}
        >
          {document.url?.toLowerCase().endsWith('.pdf') ? (
            <Worker workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}>
              <Viewer fileUrl={document.url} />
            </Worker>
          ) : (
            <img
              src={document.url}
              alt={document.name || "Document"}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          )}
        </DocumentPreviewDialog>
      )}
      {/* {!!document && (
        <ViewDocument {...document} onClose={() => setDocument(null)} />
      )} */}
    </>
  );
}
